import { useCallback, useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
  Table,
} from "antd";

import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import {
  ACCESS_VIEW,
  PERMISSION_PROJECTED_DISPLAY,
} from "../../utils/constants";
import { hasAccess } from "../../utils/form_utilities";
import ReportHeading from "../../utils/ReportHeading";
import { addKeysToArray } from "../../utils/utilities";
import { smallFormItemLayout } from "./../../utils/Layouts";
import { PrinterOutlined, TableOutlined } from "@ant-design/icons";

const { Content } = Layout;

const url = "/api/production/";
const title = "Production History Report";

const initialValues = {
  period_covered: [moment().startOf("month"), moment().endOf("month")],
  is_excel: false,
};
export default function ProductionHistory() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [loading, setLoading] = useState({
    search: false,
    download: false,
  });

  const report = useRef(null);

  const records_column = [
    // {
    //   title: "PROD#",
    //   dataIndex: "production_no",
    //   width: 50,
    // },
    {
      title: "Date",
      dataIndex: "date",
      render: (date, record) => (
        <span>{record.footer !== 1 && moment(date).format("M/D/YY")}</span>
      ),
    },
    {
      title: "Finished Product",
      dataIndex: ["finished_product", "name"],
    },
    {
      title: "Items",
      dataIndex: "items",
      render: (items, record, index) =>
        items &&
        items.map((o) => (
          <span>
            {o.stock?.name}
            <br />
          </span>
        )),
    },
    {
      title: "Qty",
      dataIndex: "items",
      align: "right",
      render: (items, record, index) =>
        items &&
        items.map((o) => (
          <span>
            {o.quantity}
            <br />
          </span>
        )),
    },
    {
      title: "Produce Quantity",
      dataIndex: "produced_quantity",
      align: "right",
    },
    ...(hasAccess({
      auth,
      access: ACCESS_VIEW,
      location: {
        pathname: PERMISSION_PROJECTED_DISPLAY,
      },
    })
      ? [
          {
            title: "Projected Quantity",
            dataIndex: "projected_quantity",
            align: "right",
          },
          {
            title: "Variance",
            dataIndex: "variance",
            align: "right",
          },
        ]
      : []),
  ];

  const getReport = useCallback(({ period_covered, is_excel = false }) => {
    const has_access = hasAccess({
      auth,
      access: ACCESS_VIEW,
      location: {
        pathname: PERMISSION_PROJECTED_DISPLAY,
      },
    });

    const form_data = {
      period_covered,
      is_excel,
      has_access,
    };

    if (period_covered) {
      setLoading((prevState) => ({
        ...prevState,
        search: !is_excel,
        download: is_excel,
      }));

      axios
        .post(`${url}history`, form_data)
        .then((response) => {
          if (response.data) {
            if (is_excel) {
              setLoading((prevState) => ({
                ...prevState,
                download: false,
              }));
              return window.open(`/public/reports/${response.data.filename}`);
            }
            setRecords(response.data);
            setLoading((prevState) => ({
              ...prevState,
              search: false,
            }));
          }
        })
        .catch((err) => {
          message.error("There was an error processing your request");
        })
        .finally(() =>
          setLoading((prevState) => ({
            ...prevState,
            search: false,
            download: false,
          }))
        );
    } else {
      message.error("Please select period covered");
    }
  }, []);

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item>{title}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div style={{ background: "#fff", padding: 24, minHeight: 280 }}>
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <RangeDatePickerFieldGroup
              label="Period Covered"
              name="period_covered"
              value={state.period_covered}
              onChange={(dates) =>
                setState((prevState) => ({
                  ...prevState,
                  period_covered: dates,
                }))
              }
              error={errors.period_covered}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
        </Row>
        <Row>
          <Col span={12} offset={4}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                    });
                  }}
                  loading={loading.search}
                  disabled={loading.search}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => {
                    getReport({
                      ...state,
                      is_excel: true,
                    });
                  }}
                  loading={loading.download}
                  disabled={loading.download}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div className="report-heading">
            <ReportHeading />
            <span className="has-text-weight-bold">{title}</span>
            <br />
            {state.period_covered?.[0] &&
              state.period_covered?.[1] &&
              `${moment(state.period_covered?.[0]).format("ll")} - ${moment(
                state.period_covered?.[1]
              ).format("ll")} `}{" "}
            <br />
            Printed By : {auth.user.name} <br />
            Date/Time Printed : {moment().format("LLL")}
          </div>
          <Table
            bordered={true}
            size="small"
            dataSource={addKeysToArray([...records])}
            columns={records_column}
            pagination={false}
            rowClassName={(record, index) => {
              if (record.footer === 1) {
                return "footer-summary has-text-weight-bold";
              }
            }}
          />
        </div>
      </div>
    </Content>
  );
}
